import {
  AnalisiDto,
  getDownloadPdfAnalisiEngQueryKey,
  getDownloadPdfAnalisiQueryKey,
  NodoDto,
  UploadedFileDto,
} from "@/api";
import Box from "@/elements/Box";
import Typography from "@/elements/Typography";
import Table from "@/elements/Table";
import moment from "moment/moment";
import { createColumnHelper } from "@tanstack/react-table";
import { ReactNode, useMemo, useState } from "react";
import FileDownload from "@/components/FileField/FileDownload";
import { useTranslation } from "react-i18next";
import WarningIcon from "@/assets/warning.png";
import ErrorIcon from "@/assets/error.png";
import { ScadenzaAnalisiWarningDays } from "@/utils/scadenzeUtils";
import { Chip, IconButton, Tooltip } from "@mui/material";
import AnalisiDialog from "./AnalisiDialog";
import EditIcon from "@mui/icons-material/Edit";
import OkIcon from "@/assets/ok.png";
import { useAuth } from "@/context/useAuth";

const columnHelper = createColumnHelper<AnalisiDto>();

export default function AnalisiTableCard({
  nodo,
  analisi,
  canEdit,
}: {
  nodo: NodoDto;
  analisi: AnalisiDto[];
  canEdit: boolean;
}) {
  const { t, i18n } = useTranslation();

  const { user } = useAuth();
  const [analisiDialogOptions, setAnalisiDialogOptions] = useState<{
    open: boolean;
    analisi?: AnalisiDto;
  }>({ open: false });

  const editing = canEdit;
  const isItalian = i18n.language.toLowerCase().startsWith("it");

  const columns = useMemo(() => {
    const columns = [
      columnHelper.accessor("data", {
        header: t("analisiTable.colonne.data"),
        cell: ({ row }) => {
          return (
            <Box
              display="flex"
              flexDirection="row"
              columnGap={1}
              alignItems="center"
            >
              {moment(row.original.data).format("L")}
              {user?.isEsterno === false &&
                row.original.aPagamento === true && (
                  <Chip
                    label={t("analisiTable.badge.aPagamento")}
                    size="small"
                  />
                )}
              {user?.isEsterno === false &&
                row.original.aPagamento === false && (
                  <Chip
                    label={t("analisiTable.badge.inclusa")}
                    size="small"
                  />
                )}
            </Box>
          );
        },
      }),

      columnHelper.accessor("esito", {
        header: t("analisiTable.colonne.esito"),
        cell: ({ getValue }) => <PallinoAnalisi esito={getValue()} />,
      }),

      columnHelper.accessor("validaFinoA", {
        header: t("analisiTable.colonne.validaFinoA"),
        cell: ({ row }) => {
          let icon: ReactNode | null = null;
          if (
            nodo.ultimaAnalisi?.id === row.original.id &&
            row.original.validaFinoA
          ) {
            const days = moment(row.original.validaFinoA).diff(
              moment(),
              "days",
              true
            );
            icon =
              days <= 0 ? (
                <img
                  src={ErrorIcon}
                  width={20}
                  alt={t("analisiTable.alt.scaduta")}
                />
              ) : days <= ScadenzaAnalisiWarningDays ? (
                <img
                  src={WarningIcon}
                  width={20}
                  alt={t("analisiTable.alt.inScadenza")}
                />
              ) : null;
          }

          return (
            <Box display="flex" gap={1} alignItems="center">
              {row.original.validaFinoA &&
                moment(row.original.validaFinoA).format("L")}
              {icon}
            </Box>
          );
        },
      }),

      columnHelper.display({
        header: t("analisiTable.colonne.pdf"),
        cell: ({ row }) => {
          const resolvedPdf = getAnalisiPdf(row.original, isItalian);

          return (
            <FileDownload
              file={resolvedPdf.file}
              downloadUrl={resolvedPdf.downloadUrl(nodo.id, row.original.id)}
            />
          );
        },
      }),

      editing &&
        columnHelper.display({
          id: "buttons",
          header: "",
          size: 100,
          maxSize: 100,
          cell: ({ row }) => {
            return (
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="flex-end"
                gap={1}
              >
                <Tooltip title={t("azioni.modifica")}>
                  <IconButton
                    onClick={() => {
                      setAnalisiDialogOptions({
                        open: true,
                        analisi: row.original,
                      });
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          },
        }),
    ].filter((x) => x);
    return columns as Exclude<(typeof columns)[number], boolean>[];
  }, [nodo.id, editing, isItalian, nodo.ultimaAnalisi?.id, t, user?.isEsterno]);

  return (
    <Box flex={1}>
      <Box display="flex" flexDirection="column" height="100%" gap={1}>
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          gap={1}
        >
          <Typography variant="h5" color="text" fontWeight="medium">
            {t("analisiTable.titolo")}
          </Typography>
        </Box>

        <Table
          columns={columns}
          data={analisi}
          initialSort={[{ id: "data", desc: true }]}
          noRowsText={t("analisiTable.noRows")}
          mt={1}
        />
      </Box>

      <AnalisiDialog
        nodo={nodo}
        open={analisiDialogOptions.open}
        analisi={analisiDialogOptions.analisi}
        onClose={() => {
          setAnalisiDialogOptions({ open: false });
        }}
      />
    </Box>
  );
}

function getAnalisiPdf(
  analisi: AnalisiDto,
  isItalian: boolean
): {
  file?: UploadedFileDto | null;
  downloadUrl: (nodoId: number, analisiId: number) => string | null;
} {
  if (isItalian) {
    return {
      file: analisi.filePdf,
      downloadUrl: (nodoId, analisiId) =>
        analisi.filePdf
          ? getDownloadPdfAnalisiQueryKey(nodoId, analisiId)[0]
          : null,
    };
  }

  if (analisi.filePdfEng) {
    return {
      file: analisi.filePdfEng,
      downloadUrl: (nodoId, analisiId) =>
        getDownloadPdfAnalisiEngQueryKey(nodoId, analisiId)[0],
    };
  }

  return {
    file: analisi.filePdf,
    downloadUrl: (nodoId, analisiId) =>
      analisi.filePdf ? getDownloadPdfAnalisiQueryKey(nodoId, analisiId)[0] : null,
  };
}

function PallinoAnalisi({ esito }: { esito: string | null | undefined }) {
  const icon =
    esito === "VERDE"
      ? OkIcon
      : esito === "GIALLO"
      ? WarningIcon
      : esito === "ROSSO"
      ? ErrorIcon
      : null;

  if (!icon) {
    return <Typography>{esito}</Typography>;
  }

  return <img src={icon} width={20} height={20} alt={esito ?? ""} />;
}
